import { useEffect, useMemo, useState } from "react";
import { ArrowDownUp, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DOWNLOAD_LINK } from "@/lib/constants";

const RATES = {
  USDT: 1355,
  USDC: 1355,
  BTC: 60_589_000,
  ETH: 2_254_165,
  BNB: 417_851,
  SOL: 187_212,
  TRX: 160.23,
  XRP: 718.93,
};

const CRYPTO_OPTIONS = Object.keys(RATES);

export default function RateCalculator() {
  const [amount, setAmount] = useState("1000");
  const [crypto, setCrypto] = useState("USDT");
  const [countdown, setCountdown] = useState(45);

  useEffect(() => {
    const t = setInterval(() => {
      setCountdown((c) => (c <= 1 ? 45 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const rate = RATES[crypto];
  const ngn = useMemo(() => {
    const a = parseFloat(amount || "0");
    if (Number.isNaN(a)) return 0;
    return a * rate;
  }, [amount, rate]);

  const formatN = (n) =>
    n.toLocaleString("en-NG", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

  return (
    <section id="rates" className="py-20 md:py-28 bg-slate-50 border-y border-slate-200 relative overflow-hidden" data-testid="rate-calculator-section">
      <div className="absolute -right-32 top-10 w-96 h-96 rounded-full bg-[#3B6FBF]/10 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#3B6FBF] font-bold mb-4">
              Live rates
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.05] text-slate-900" data-testid="rate-calculator-title">
              Get the best rate
              <br /> in seconds.
            </h2>
            <p className="mt-5 text-slate-600 max-w-md">
              Check your conversion instantly. No login required. The rate you see is the rate you get.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-slate-700">
              {[
                "Real-time pricing across 12+ assets",
                "No hidden conversion fees",
                "Rate match guarantee — show us a better one",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#3B6FBF]" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(59,111,191,0.35)]" data-testid="rate-calculator-card">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono-data">PamaPay Live Rate</div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-mono-data">
                  <RefreshCw className="w-3 h-3" />
                  refresh in {countdown}s
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-1.5">You send</div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="font-mono-data text-2xl sm:text-3xl font-bold bg-transparent border-none p-0 focus-visible:ring-0 text-slate-900 h-auto"
                    data-testid="rate-input-amount"
                  />
                  <Select value={crypto} onValueChange={setCrypto}>
                    <SelectTrigger className="w-[120px] bg-white border-slate-300 rounded-full" data-testid="rate-select-crypto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-900">
                      {CRYPTO_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`rate-option-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-center -my-1.5 relative z-10">
                <div className="w-10 h-10 bg-[#3B6FBF] rounded-full flex items-center justify-center ring-4 ring-white">
                  <ArrowDownUp className="w-4 h-4 text-white" />
                </div>
              </div>

              <div className="rounded-2xl border border-[#3B6FBF]/30 bg-gradient-to-br from-[#3B6FBF]/10 to-transparent p-4">
                <div className="text-[10px] uppercase text-slate-600 tracking-wider mb-1.5">You receive</div>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono-data text-2xl sm:text-3xl font-bold text-slate-900" data-testid="rate-output-ngn">
                    ₦{formatN(ngn)}
                  </div>
                  <div className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-900">NGN</div>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between text-xs">
                <span className="text-slate-500">Rate</span>
                <span className="font-mono-data text-slate-900" data-testid="rate-display">
                  1 {crypto} = ₦{rate.toLocaleString("en-NG")}
                </span>
              </div>

              <a
                href={DOWNLOAD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#3B6FBF] hover:bg-[#2f5ca3] text-white font-semibold rounded-full py-3.5 transition-all"
                data-testid="rate-calculator-cta"
              >
                Lock this rate in the app
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
